import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import Footer from "@/components/Footer";
import ProvincePicker from "@/components/ProvincePicker";
import { ErrorView, FormView, SuccessView } from "@/components/survey";
import { clearAllCaches } from "@/utils/cacheAndVersion";
import { getNextOrCurrentEvent } from "@/utils/events";
import { getFormInstance } from "@/utils/form";

import type { Field, PublicFormInstance } from "@/utils/form";

const Survey = () => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  const [currentInstanceKey, setCurrentInstanceKey] = useState<string | null>(
    null,
  );
  const [eventNotFound, setEventNotFound] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const [conciergePlusUrl, setConciergePlusUrl] = useState<string | null>(null);
  const [formInstance, setFormInstance] = useState<PublicFormInstance | null>(
    null,
  );
  const [formError, setFormError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [clearingCache, setClearingCache] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [timer, setTimer] = useState(30);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleReset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTimer(30);

    setConciergePlusUrl(null);
    setCurrentInstanceKey(null);

    setIsCompleted(false);
    setFormError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getSurveyFields = useCallback(async (instanceKey: string) => {
    const instance = await getFormInstance(instanceKey);

    if (!instance) {
      setFormError({
        title: "Could not load survey form",
        message: "Please refresh the page and try again.",
      });
      return;
    }

    const { fields } = instance;
    setFormInstance({
      ...instance,
      fields: fields.filter((field: Field) => !field.hidden),
    });
  }, []);

  const onSuccess = useCallback(
    (message?: string) => {
      if (message) {
        setFormError({
          title: "Survey data has been submitted but:",
          message,
        });
      }

      setIsCompleted(true);
      window.scrollTo({ top: 0, behavior: "instant" });

      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev === 0) return 0;
          const newTimer = prev - 1;
          if (newTimer === 0) {
            handleReset();
          }
          return newTimer;
        });
      }, 1000);
    },
    [handleReset],
  );

  const openConciergePlus = () => {
    if (conciergePlusUrl) {
      window.open(conciergePlusUrl, "_blank");
    }
  };

  const fetchNextEvent = async (province: string) => {
    if (loadingEvent) return;
    setLoadingEvent(province);
    setEventNotFound(false);

    try {
      const nextEvent = await getNextOrCurrentEvent(province);

      if (nextEvent) {
        const surveyKey = nextEvent.instance_key.replace(
          "FORD_MODEL_E_DIGITAL_ACTIVATION",
          "FORD_MODEL_E_SURVEY",
        );
        setCurrentInstanceKey(surveyKey);
        await getSurveyFields(surveyKey);
        setLoadingEvent(null);
      } else {
        setEventNotFound(true);
        setLoadingEvent(null);
      }
    } catch (error) {
      Sentry.captureException(error);
      setFormError({
        title: "Could not load next event.",
        message: (error as Error).message,
      });
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    const alertResponse = window.confirm(
      "Are you sure you want to clear the cache? The page will be reloaded and you will lose any unsaved data.",
    );
    if (alertResponse) {
      try {
        await clearAllCaches();
        window.location.reload();
      } catch (error: unknown) {
        console.error("Error clearing cache:", error);
        Sentry.captureException(error);
        setClearingCache(false);
        setFormError({
          title: "Could not clear cache.",
          message: (error as Error).message,
        });
      }
    } else {
      setClearingCache(false);
    }
  };

  const currentView = useMemo(() => {
    if (!currentInstanceKey) return "intro";
    if (isCompleted) return "success";
    if (formError) return "error";
    return "form";
  }, [currentInstanceKey, isCompleted, formError]);

  useEffect(() => {
    if (formError) {
      setTimeout(() => {
        const errorMessage = document.getElementById("error-message");
        if (errorMessage) {
          errorMessage.scrollIntoView({ behavior: "smooth" });
        }
      }, 200);
    }
  }, [formError]);

  useEffect(() => {
    const instance = searchParams.get("instance");

    if (instance) {
      setCurrentInstanceKey(instance);
      getSurveyFields(instance);
      setLoadingEvent(null);
      return;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return (
    <main>
      <div className="p-[30px] min-h-[calc(100dvh-290px)]">
        <SwitchTransition>
          <CSSTransition
            key={currentView}
            nodeRef={nodeRef}
            timeout={300}
            classNames="slideIn"
            unmountOnExit
          >
            <div ref={nodeRef}>
              {currentView === "intro" && (
                <ProvincePicker
                  setProvince={fetchNextEvent}
                  loadingEvent={loadingEvent}
                  notFound={eventNotFound}
                />
              )}

              {currentView === "success" && (
                <SuccessView
                  conciergePlusUrl={conciergePlusUrl}
                  timer={timer}
                  onOpenConciergePlus={openConciergePlus}
                  onReset={handleReset}
                />
              )}

              {currentView === "form" && (
                <FormView
                  formInstance={formInstance}
                  currentInstanceKey={currentInstanceKey}
                  onSuccess={onSuccess}
                  onError={setFormError}
                  onReset={handleReset}
                  onSetConciergePlusUrl={setConciergePlusUrl}
                />
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>

        {formError && (
          <ErrorView
            formError={formError}
            clearingCache={clearingCache}
            onClearCache={handleClearCache}
          />
        )}
      </div>
      <Footer isSurvey />
    </main>
  );
};

export default Survey;
